import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { firstDayOfWeek, addDuration, getDuration } from "../../../_helpers/functions";
import { Button } from "../../../_helpers/components/Button";
import { getInterventions, updateIntervention, createIntervention } from "../../../_api/Intervention";
import { getInterventionsCategories } from "../../../_api/InterventionCategorie";
import { toast } from "react-hot-toast";
import { dateToAAAAMMJJ, dateUTCToLocal } from "../../../_helpers/functions";
import { InterventionsPlanificationModal } from "./InterventionsPlanificationModal";
import { InterventionsDetailModal } from "./InterventionsDetailModal";
import { getUsers } from "../../../_api/Admin";
import { Filter } from "../../../_helpers/components/Forms/Filter";
import { InterventionsCreationModal } from "./InterventionsCreationModal";
import { cssColor } from "./InterventionsColors";

export const InterventionsPlanning = () => {
    const [user, setUser, currentApp, currentMenu] = useOutletContext();
    const [unplanned, setUnplanned] = useState([]);
    const [planned, setPlanned] = useState([]);
    const [done, setDone] = useState([]);
    const [isUnplannedOpenMobile, setIsUnplannedOpenMobile] = useState(true);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [families, setFamilies] = useState([]);
    const [filter, setFilter] = useState({ priority: "ALL", team: "-1", family: "ALL", category: "-1", performer: "-1" });
    const [currentWeekStart, setCurrentWeekStart] = useState(firstDayOfWeek(new Date()));
    const [intDuree, setIntDuree] = useState("");
    const [hour, setHour] = useState("");
    // const [showPlanModal, setShowPlanModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showClotureModal, setShowClotureModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [intUser, setIntUser] = useState("");
    const [remarks, setRemarks] = useState("");
    const [ticketStatus, setTicketStatus] = useState("");
    const [duplicate, setDuplicate] = useState(false);
    const [usersEnroll, setUsersEnroll] = useState([]);

    const availableTeams = user.teams.sort((a, b) => (a.name < b.name ? -1 : 1));
    // Récupération des features depuis TASK_LIST
    const features_right = user.policy.rights[currentApp]["TASK_LIST"]["sub_menu"]["MAIN"]["features"];

    const hMin = 6;
    const hPerDay = 13;

    const priority = {
        ALL: {
            label: "Toutes",
            color: cssColor.ALL,
        },
        URGENT: {
            label: "Urgent",
            color: cssColor.URGENT,
        },
        IMPORTANT: {
            label: "Important",
            color: cssColor.IMPORTANT,
        },
        NORMALE: {
            label: "Normale",
            color: cssColor.NORMALE,
        },
        MEMO: {
            label: "Pour mémo",
            color: cssColor.MEMO,
        },
    }

    const status = {
        OPEN: {
            label: "Ouvert",
            color: cssColor.OPEN,
        },
        ONGOING: {
            label: "En cours",
            color: cssColor.ONGOING,
        },
        PENDING: {
            label: "En attente",
            color: cssColor.PENDING,
        },
        CLOSED: {
            label: "Terminé",
            color: cssColor.CLOSED,
        },
        UNREALIZABLE: {
            label: "Non réalisable",
            color: cssColor.UNREALIZABLE,
        },
    }

    const defaultTicket = {
        title: "",
        description: "",
        priority: "NORMALE",
        status: "OPEN",
        due_date: undefined,
        start_time: null,
        completion_time: null,
        requester_id: null,
        performer_id: null,
        team_id: availableTeams.length > 0 ? availableTeams[0].id : null,
        category_id: categories.length > 0 ? categories[0].id : null,
    };

    const [newTicket, setNewTicket] = useState(defaultTicket);

    function getWeekDays(startDate) {
        return [...Array(7)].map((_, i) => {
            const d = new Date(startDate);
            d.setDate(d.getDate() + i);
            return d;
        });
    }

    // 🔹 Charger les tickets
    const fetchTickets = async () => {
        setLoading(true);
        try {
            const resp = await getInterventions();
            setTickets(resp.data);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des interventions.");
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Charger les tickets
    const fetchCategories = async () => {
        setLoading(true);
        try {
            const resp = await getInterventionsCategories();
            setCategories(resp.data);
            setFamilies(Array.from(new Set(resp.data.map((c) => { return c.family }))));
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des catégories d'interventions.");
        } finally {
            setLoading(false);
        }
    };

    // Chargement des users
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const resp = await getUsers();
            setUsersEnroll(resp.data.filter((usr) => usr.policy.rights[currentApp]["TASK_LIST"]["sub_menu"]["MAIN"]["features"]["task_enroll"] === true))
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des utilisateurs.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchCategories();
        fetchUsers();
        fetchTickets();
    }, []);

    // 🔹 Filtrage
    const filteredTickets = tickets.filter((t) => {
        const matchPriority = filter.priority === "ALL" || t.priority === filter.priority;
        const matchTeam = filter.team === "-1" || t.team_id?.toString() === filter.team;
        const matchFamily = filter.family === "ALL" || t.category.family === filter.family;
        const matchCategory = filter.category === "-1" || t.category_id?.toString() === filter.category;
        const matchPerformer = filter.performer === "-1" || t.performer_id?.toString() === filter.performer;
        return matchPriority && matchTeam && matchFamily && matchCategory && matchPerformer;
    });

    useEffect(() => {
        setUnplanned(filteredTickets.filter((t) => ["OPEN", "PENDING"].includes(t.status)));
        setPlanned(filteredTickets.filter((t) => t.status == "ONGOING").sort((a, b) => a.start_time < b.start_time ? -1 : 1));
        setDone(filteredTickets.filter((t) => ["CLOSED", "UNREALIZABLE"].includes(t.status)).sort((a, b) => a.start_time < b.start_time ? -1 : 1));
    }, [tickets, filter]);

    const handlePrevWeek = () => {
        const prev = new Date(currentWeekStart);
        prev.setDate(prev.getDate() - 7);
        setCurrentWeekStart(firstDayOfWeek(prev));
    };

    const handleNextWeek = () => {
        const next = new Date(currentWeekStart);
        next.setDate(next.getDate() + 7);
        setCurrentWeekStart(firstDayOfWeek(next));
    };

    // Drag & drop
    const handleDragStart = (ticket) => setSelectedTicket(ticket);

    const handleDragOver = (e, date) => {
        e.preventDefault();
        const height = e.currentTarget.offsetHeight;
        const heightHour = height / hPerDay;
        const mouseY = heightHour > 0 ? Math.round((e.clientY - e.currentTarget.offsetTop) / heightHour) : 0;
        setHour(hMin + mouseY);
        setSelectedDate(date);
    }

    const handleDragExit = (e) => {
        setHour("");
    }

    const handleDropAndConfirm = async (e, date) => {
        setHour("");
        if (!selectedTicket) return;
        const aHour = new Date();
        const height = e.currentTarget.offsetHeight;
        const heightHour = height / hPerDay;
        const mouseY = heightHour > 0 ? Math.round((e.clientY - e.currentTarget.offsetTop) / heightHour) : 0;
        aHour.setHours(hMin + mouseY, 0, 0);
        setSelectedTime(selectedTicket.start_time ? new Date(selectedTicket.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : aHour.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        setIntDuree("01:00");

        const startTime = dateUTCToLocal(date);
        startTime.setHours(aHour.getHours());
        const endTime = addDuration(startTime, "01:00");
        const aTicket = { ...selectedTicket, start_time: startTime.toISOString(), completion_time: endTime.toISOString(), status: "ONGOING", performer_id: user.id, performer: usersEnroll.find((u) => u.id == user.id) };
        await updateIntervention({ ...aTicket });

        const updatedTickets = tickets.map((t) =>
            t.id === selectedTicket.id ? { ...aTicket } : t
        );
        setTickets(updatedTickets);

        // setShowPlanModal(false);
        setSelectedTicket(null);
        setSelectedTime("");
    };

    const handleRemoveFromDay = async (ticketId) => {
        const ticket = planned.find((t) => t.id === ticketId);
        const aTicket = { ...ticket, start_time: null, completion_time: null, status: "OPEN", performer_id: null, performer: null };
        await updateIntervention({ ...aTicket });
        const updatedTickets = tickets.map((t) =>
            t.id === aTicket.id ? { ...aTicket } : t
        );
        setTickets(updatedTickets);
    };

    // Modal avec le détail
    const handleOpenDetailModal = (ticket) => {
        setSelectedTicket(ticket);
        setSelectedDate(dateToAAAAMMJJ(ticket.start_time, '-'));
        setSelectedTime(new Date(ticket.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        if (ticket.completion_time) {
            setIntDuree(getDuration(ticket.start_time, ticket.completion_time));
        } else {
            setIntDuree("01:00");
        }
        setShowDetailModal(true);
    };

    // 🔹 Ouvrir la modale de clôture
    const handleOpenUpdateModal = (ticket, newStatus = ticket.status) => {

        setSelectedTicket(ticket);
        setSelectedDate(ticket.start_time ? dateToAAAAMMJJ(ticket.start_time, "-") : dateToAAAAMMJJ(new Date(), "-"));
        setSelectedTime(ticket.start_time ? new Date(ticket.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        if (ticket.completion_time) {
            setIntDuree(getDuration(ticket.start_time, ticket.completion_time));
        } else {
            setIntDuree("01:00");
        }
        setRemarks("");
        setIntUser(ticket.performer_id ?? user.id);
        setTicketStatus(newStatus);
        setDuplicate(false);
        setShowClotureModal(true);

    };

    const handleUpdateIntervention = async () => {
        if (!selectedDate || !selectedTime) return toast.error("Merci de renseigner la date et l’heure de début d'intervention");
        if (!intDuree) return toast.error("Merci de renseigner la durée de l'intervention");
        if (ticketStatus != "OPEN" && !intUser) return toast.error("La planification de l'intervention implique de définir une attribution.");
        if (!["OPEN", "ONGOING"].includes(ticketStatus) && !remarks) return toast.error("Merci de renseigner le résultat de l'intervention");
        setIsSaving(true);
        try {
            const startTime = new Date(selectedDate);
            startTime.setHours(selectedTime.slice(0, 2), selectedTime.slice(3, 5), 0);
            const endTime = addDuration(startTime, intDuree);
            const rmk = remarks ?
                remarks + (selectedTicket.remarks ? (`\n========== ${dateToAAAAMMJJ(selectedTicket.updated_at, "-")} ${new Date(selectedTicket.updated_at).toLocaleTimeString([])} ========== \n` + selectedTicket.remarks) : "")
                :
                selectedTicket.remarks
                ;
            const performer = usersEnroll.find((u) => u.id == intUser);
            const aTicket = { ...selectedTicket, start_time: new Date(startTime).toISOString(), completion_time: new Date(endTime).toISOString(), status: ticketStatus, performer_id: intUser, performer: performer, remarks: rmk };
            await updateIntervention({ ...aTicket });

            const updatedTickets = tickets.map((t) =>
                t.id === selectedTicket.id ? { ...aTicket } : t
            );
            setTickets(updatedTickets);

            toast.success("Intervention mise à jour.");

            // Fermeture des modales
            setShowDetailModal(false);
            setShowClotureModal(false);
            if (duplicate) {
                handleDuplicate(aTicket);
            }
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de la planification");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDuplicate = (ticket) => {
        const aTicket = ({
            ...defaultTicket,
            title: ticket.title,
            description: ticket.description,
            priority: ticket.priority,
            requester_id: user.id,
            performer_id: ticket.performer_id,
            category_id: ticket.category_id,
        })
        setNewTicket(aTicket);
        setShowDetailModal(false);
        setShowClotureModal(false);
        setShowCreateModal(true);
    }

    // 🔹 Gestion du formulaire
    const handleChange = (e, rubrique) => {
        setNewTicket({ ...newTicket, [rubrique]: e.target.value });
    };

    // 🔹 Création de ticket
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newTicket.title.trim()) return toast.error("L'objet est obligatoire");
        if (!newTicket.description.trim()) return toast.error("La description est obligatoire");

        setLoading(true);
        const aTicket = { ...newTicket, requester_id: user.id };
        try {
            await createIntervention({ ...aTicket });
            toast.success("Intervention créée avec succès");
            setShowCreateModal(false);
            setNewTicket(defaultTicket);
            await fetchTickets();
        } catch (err) {
            console.error(err);
            toast.error("Impossible de créer l'intervention");
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = ((rub, val) => {
        setFilter({ ...filter, [rub]: val })
    });

    const weekDays = getWeekDays(currentWeekStart);

    const statusInput = () => {
        return {
            value: ticketStatus,
            options: [...Object.keys(status).map(s => ({ label: status[s].label, value: s }))],
            setValue: setTicketStatus,
        };
    }

    return (
        <>
            <div className="flex flex-col grid-cols-1 p-6 space-y-6 h-full">
                {/* Popup lors du drag 'n drop */}
                {hour &&
                    <div className="fixed text-center w-96 self-center-safe justify-center p-4 mx-auto bg-app-secondary/20 border border-app-primary text-app-primary rounded-sm">
                        Planifier l'intervention le <span className="font-bold">{new Date(selectedDate).toLocaleDateString([], { weekday: "short", day: "2-digit", month: "2-digit", })}</span> à <span className="font-bold">{hour}h</span>
                    </div>
                }

                {/* Header */}
                <div className="flex flex-wrap justify-between w-full gap-2 flex-col lg:flex-row">
                    {/* Filtres */}
                    <Filter
                        buttonLabel={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                            </svg>

                        }
                        actualFilter={filter}
                        withLabel={true}
                        className={"flex flex-col gap-2 mb-6"}
                        listFilters={[
                            {
                                name: "priority",
                                label: "Priorité",
                                options: [...Object.keys(priority).map(p => ({ label: priority[p].label, value: p }))],
                                value: filter.priority,
                            },
                            {
                                name: "team",
                                label: "Service",
                                options: [{ label: "Tous", value: "-1" }, ...availableTeams.map(t => ({ label: t.name, value: t.id }))],
                                value: filter.team,
                            },
                            {
                                name: "family",
                                label: "Famille",
                                options: [{ label: "Toutes", value: "ALL" }, ...families.map(f => ({ label: f, value: f }))],
                                value: filter.family,
                            },
                            {
                                name: "category",
                                label: "Catégorie",
                                options: [{ label: "Toutes", value: "-1" }, ...categories.map(t => ({ label: t.family + " > " + t.label, value: t.id }))],
                                value: filter.category,
                            },
                            {
                                name: "performer",
                                label: "Assigné à",
                                options: [{ label: "Tous", value: "-1" }, ...usersEnroll.map(u => ({ label: (u.last_name ?? "") + " " + (u.first_name ?? ""), value: u.id }))],
                                value: filter.performer,
                            },
                        ]}
                        handleChange={handleFilter}
                    />

                    {/* Visible en mode navigateur */}
                    <div className="hidden lg:flex gap-2">
                        <Button
                            action={handlePrevWeek}
                            name={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
                                </svg>
                            }
                        />
                        <Button
                            action={handleNextWeek}
                            name={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                                </svg>

                            }
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-gray-500">Chargement...</div>
                ) :
                    <div className="flex flex-col lg:flex-row gap-6 col-span-1 lg:col-span-2 h-full lg:overflow-hidden">
                        {/* Liste des tickets non planifiés */}
                        {features_right["task_enroll"] &&
                            <div className="border rounded-lg p-4 shadow-sm min-w-2xs lg:max-w-2xs lg:overflow-hidden">
                                <div className="w-full font-bold text-gray-700 mb-4 bg-white flex items-center justify-between gap-2">
                                    <span>🕓 En attente</span>
                                    <button
                                        type="button"
                                        className="lg:hidden text-gray-600 hover:text-gray-900 select-none"
                                        aria-expanded={isUnplannedOpenMobile}
                                        aria-controls="unplanned-list"
                                        onClick={() => setIsUnplannedOpenMobile((v) => !v)}
                                        title={isUnplannedOpenMobile ? "Replier" : "Déplier"}
                                    >
                                        <span className="text-sm font-normal">
                                            {isUnplannedOpenMobile ? "▲" : "▼"}
                                        </span>
                                    </button>
                                </div>
                                <div
                                    id="unplanned-list"
                                    className={`space-y-3 lg:overflow-y-scroll max-h-[90%] ${isUnplannedOpenMobile ? "block" : "hidden"} lg:block`}
                                >
                                    {unplanned.length > 0 ? (
                                        unplanned.map((ticket) => (
                                            <div
                                                key={ticket.id}
                                                draggable={ticket.performer == null || ticket.performer_id == user.id}
                                                onDragStart={() => handleDragStart(ticket)}
                                                className={`bg-white border rounded-md p-3 ${ticket.performer == null || ticket.performer_id == user.id ? "cursor-grab active:cursor-grabbing" : "cursor-default"} hover:bg-gray-100 transition overflow-hidden`}
                                            >
                                                {ticket.team && (
                                                    <div
                                                        className="mb-2 h-1 -mt-3 -mx-4 rounded-full"
                                                        style={{
                                                            backgroundColor: ticket.team.bg_color,
                                                            opacity: "70%",
                                                        }}
                                                    >
                                                    </div>
                                                )}
                                                <div className="flex justify-between">
                                                    <div className="text-base font-semibold text-gray-800 text-ellipsis text-nowrap overflow-hidden">
                                                        {ticket.title}
                                                    </div>
                                                    <button
                                                        onClick={() => handleOpenUpdateModal(ticket)}
                                                        title="Planifier l'intervention"
                                                        className={`cursor-pointer ${ticket.performer === null || ticket.performer_id == user.id ? "block" : "hidden"} lg:hidden`}
                                                    >
                                                        📅
                                                    </button>
                                                </div>
                                                {ticket.performer !== null &&
                                                    <span className="text-xs text-gray-600">{"➡️ " + (ticket.performer?.last_name || "") + " " + (ticket.performer?.first_name || "")}</span>
                                                }
                                                {ticket.category &&
                                                    <span className="text-xs italic text-gray-500 mb-2 line-clamp-2">{ticket.category.label}</span>
                                                }
                                                <span className={`text-xxs px-2 py-1 rounded-full border ${priority[ticket.priority].color}`}>{priority[ticket.priority].label}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-400">Aucun ticket en attente</p>
                                    )}
                                </div>
                            </div>
                        }

                        {/* Visible en mode mobile */}
                        <div className="lg:hidden flex justify-center gap-2">
                            <Button
                                action={handlePrevWeek}
                                name={
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
                                    </svg>
                                }
                            />
                            <Button
                                action={handleNextWeek}
                                name={
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                                    </svg>

                                }
                            />
                        </div>

                        {/* Planning de la semaine */}
                        <div className="col-span-4 grid lg:grid-cols-5 lg:grid-rows-[4fr_1fr] gap-3 grow pb-4 lg:pb-0">
                            {weekDays.map((date) => {
                                const aDateAAAAMMJJ = dateToAAAAMMJJ(date, '-');
                                const todayPlanned = planned ? planned.filter((p) => dateToAAAAMMJJ(p.start_time, '-') == aDateAAAAMMJJ) : [];
                                const todayDone = done ? done.filter((d) => dateToAAAAMMJJ(d.start_time, '-') == aDateAAAAMMJJ) : [];
                                
                                return (
                                    <div
                                        key={aDateAAAAMMJJ}
                                        onDragOver={(e) => handleDragOver(e, aDateAAAAMMJJ)}
                                        onDragExit={(e) => handleDragExit(e)}
                                        onDrop={(e) => handleDropAndConfirm(e, aDateAAAAMMJJ)}
                                        className="bg-gray-50 grow min-h-10 border-2 border-dashed border-gray-300 rounded-lg p-3 shadow-sm hover:border-app-primary/50 hover:bg-app-secondary/20 transition overflow-y-auto"
                                    >
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2 text-center">
                                            {date.toLocaleDateString([], {
                                                weekday: "short",
                                                day: "2-digit",
                                                month: "2-digit",
                                            })}
                                        </h4>
                                        {todayPlanned.length === 0 && todayDone.length === 0 && features_right["task_enroll"] && (
                                            <p className="text-xs text-gray-400 text-center italic invisible lg:visible">
                                                Glisser ici
                                            </p>
                                        )}
                                        <div className="space-y-2">
                                            {todayPlanned.map((t) => (
                                                <div
                                                    key={t.id}
                                                    className={"border group hover:shadow-sm rounded-md px-3 py-2 text-sm flex justify-between items-center" + (t.performer_id == user.id && " cursor-grab")}
                                                    style={{
                                                        backgroundColor: t.team.bg_color + "80",
                                                    }}
                                                    draggable={t.performer_id == user.id}
                                                    onDragStart={() => handleDragStart(t)}
                                                >
                                                    <div className="text-xs overflow-hidden">
                                                        <div className="text-gray-800 cursor-pointer hover:underline overflow-hidden text-ellipsis" onClick={() => handleOpenDetailModal(t)}>{t.title}</div>
                                                        {t.start_time && (
                                                            <div className="text-gray-500">{new Date(t.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                        )}
                                                    </div>
                                                    {t.performer_id == user.id &&
                                                        <div className="invisible group-hover:visible">
                                                            <button
                                                                onClick={() => handleRemoveFromDay(t.id)}
                                                                className="text-xs text-red-500 hover:text-red-700 cursor-pointer"
                                                                title="Retirer"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    }
                                                </div>
                                            ))}

                                            {/* Séparateur */}
                                            {(todayPlanned.length > 0 && todayDone.length > 0) &&
                                                <div className="h-px bg-gray-300"></div>
                                            }

                                            {todayDone.map((t) => (
                                                <div
                                                    key={t.id}
                                                    className={`border rounded-md px-3 py-2 text-sm flex justify-between items-center`}
                                                    style={{
                                                        backgroundColor: t.team.bg_color + "30",
                                                    }}
                                                >
                                                    <div className="text-xs overflow-hidden">
                                                        <div className="text-gray-600 cursor-pointer hover:underline overflow-hidden text-ellipsis" onClick={() => handleOpenDetailModal(t)}>{t.title} <span className="text-xxs">(terminée)</span></div>
                                                        {t.start_time && (
                                                            <div className="text-gray-500">{new Date(t.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                }
            </div>

            {/* 🔹 Modal de création - utilisée pour dupliquer un ticket */}
            {showCreateModal && (
                <InterventionsCreationModal
                    newTicket={newTicket}
                    handleSubmit={handleSubmit}
                    setVisible={setShowCreateModal}
                    handleChange={handleChange}
                    categories={categories}
                    priorities={priority}
                    teams={availableTeams}
                    // usersCanEnroll={features_right["task_force_enroll"] ? usersEnroll : undefined}
                    loading={loading}
                />
            )}

            {/* 🔹 Modal de planification */}
            {/* {showPlanModal &&
                <InterventionsPlanificationModal
                    title="Planifier l’intervention"
                    handleSave={confirmDrop}
                    setVisible={setShowPlanModal}
                    dateInput={{ label: "Date", value: selectedDate, setValue: setSelectedDate, isRequired: true }}
                    hourInput={{ label: "Heure d'intervention", value: selectedTime, setValue: setSelectedTime, isRequired: true }}
                    durationInput={{ label: "Durée (prévisionnelle)", value: intDuree, setValue: setIntDuree, isRequired: true }}
                />
            } */}

            {/* 🔹 Modal de clôture */}
            {/* {showClotureModal &&
                <InterventionsPlanificationModal
                    title="Mettre à jour l’intervention"
                    handleSave={handleUpdateIntervention}
                    setVisible={setShowClotureModal}
                    loading={isSaving}
                    dateInput={{ label: "Date de l'intervention", value: selectedDate, setValue: setSelectedDate, isRequired: true }}
                    hourInput={{ label: "à", value: selectedTime, setValue: setSelectedTime, isRequired: true }}
                    durationInput={{ label: "Durée", value: intDuree, setValue: setIntDuree, isRequired: true }}
                    remarksInput={{ label: "Remarques", value: remarks, setValue: setRemarks, isRequired: true }}
                    duplicateInput={{ label: "Créer une nouvelle intervention", value: duplicate, setValue: setDuplicate }}
                />
            } */}

            {showClotureModal &&
                <InterventionsPlanificationModal
                    title="Mettre à jour l’intervention"
                    handleSave={handleUpdateIntervention}
                    setVisible={setShowClotureModal}
                    loading={isSaving}
                    dateInput={{ label: "Date de l'intervention", value: selectedDate, setValue: setSelectedDate, isRequired: true }}
                    hourInput={{ label: "à", value: selectedTime, setValue: setSelectedTime, isRequired: true }}
                    durationInput={{ label: "Durée", value: intDuree, setValue: setIntDuree, isRequired: true }}
                    usersCanEnroll={features_right["task_force_enroll"] ? { options: usersEnroll, value: intUser, setValue: setIntUser } : undefined}
                    remarksInput={{ label: "Remarques", value: remarks, setValue: setRemarks, isRequired: true }}
                    duplicateInput={{ label: "Créer une nouvelle intervention", value: duplicate, setValue: setDuplicate }}
                    statusInput={statusInput()}
                />
            }

            {/* 🔹 Modal détail */}
            {showDetailModal &&
                <InterventionsDetailModal
                    intervention={selectedTicket}
                    setVisible={setShowDetailModal}
                    // handleOpenPlanModal={setShowPlanModal}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    canEnroll={(features_right["task_enroll"] || features_right["task_force_enroll"])}
                    userId={user.id}
                    handleDuplicate={features_right["task_create"] === true ? handleDuplicate : null}
                />
            }


        </>
    );
}
